import { ClassSetup, FeedbackCategory, Question } from '../types/setup';
import { questionBank } from './questionBank';

export interface FeedbackPlan {
  classNumber: number;
  questions: Question[];
}

function getQuestionsForCategory(categoryId: string, count: number, questionType?: 'likert' | 'open-ended'): Question[] {
  const categoryQuestions = questionBank.filter(q => 
    q.categoryId === categoryId && 
    (questionType ? q.type === questionType : true)
  );
  
  const shuffled = [...categoryQuestions].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function calculateQuestionDistribution(
  selectedCategories: FeedbackCategory[],
  questionsPerSession: number,
  currentWeek: number
): Map<string, number> {
  // Return empty map if no categories are selected
  if (selectedCategories.length === 0) {
    return new Map<string, number>();
  }

  const distribution = new Map<string, number>();
  
  // Filter categories for current week
  const categoriesForWeek = selectedCategories.filter(cat => cat.week === currentWeek);
  
  if (categoriesForWeek.length === 0) {
    return distribution;
  }

  // Distribute questions evenly among categories for this week
  const questionsPerCategory = Math.floor(questionsPerSession / categoriesForWeek.length);
  const remainingQuestions = questionsPerSession % categoriesForWeek.length;

  categoriesForWeek.forEach((category, index) => {
    // Add extra question to early categories if there are remaining questions
    const extraQuestion = index < remainingQuestions ? 1 : 0;
    distribution.set(category.id, questionsPerCategory + extraQuestion);
  });

  return distribution;
}

export function generateFeedbackPlan(setup: ClassSetup): FeedbackPlan[] {
  const selectedCategories = setup.selectedCategories.filter(cat => cat.selected);
  if (selectedCategories.length === 0) {
    return [];
  }

  const plan: FeedbackPlan[] = [];
  
  // Calculate classes per week based on frequency
  const classesPerWeek = setup.frequency === 'weekly' ? 1 : 
                        setup.frequency === 'biweekly' ? 0.5 : 
                        setup.frequency === 'daily' ? 5 : 1;
  
  // Generate feedback plan for each class
  for (let classNum = 1; classNum <= setup.totalClasses; classNum++) {
    // Calculate current week
    const currentWeek = Math.ceil(classNum / classesPerWeek);
    
    const questionDistribution = calculateQuestionDistribution(
      selectedCategories,
      setup.questionsPerSession,
      currentWeek
    );

    // Skip if no questions are distributed for this class
    if (questionDistribution.size === 0) {
      continue;
    }

    const classQuestions: Question[] = [];

    // Get questions for each category based on distribution
    questionDistribution.forEach((count, categoryId) => {
      // Always include at least one open-ended question if we have more than one question
      if (count > 1) {
        classQuestions.push(...getQuestionsForCategory(categoryId, 1, 'open-ended'));
        classQuestions.push(...getQuestionsForCategory(categoryId, count - 1, 'likert'));
      } else {
        classQuestions.push(...getQuestionsForCategory(categoryId, count));
      }
    });

    plan.push({
      classNumber: classNum,
      questions: classQuestions
    });
  }

  return plan;
}